import { useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import { PageContainer, PageHeader } from "../components/PageHeader";
import AssetsPicker from "../components/AssetsPicker";
import {
  FieldControl,
  previewSlide,
  withPicked,
} from "../components/SignageSlideDialog";
import TemplateSlide from "../../tv/signage/TemplateSlide";
import { tokens } from "../theme";
import { foyerApi, type SignageScopeRef } from "../http/foyer";
import type {
  SignageTemplate,
  SignageTemplateField,
  SignageTemplateInstance,
  SignageTemplateRegion,
} from "@shared/types/signage-template";

/**
 * Update screens - the care unit's page.
 *
 * ## Who this is for
 *
 * The lowest signage tier, `canEditSignageContent`: a ward's own staff. The
 * content owner designed the screen and scheduled it; the ward changes today's
 * lunch, the activity list, who is on shift. So this page does ONE thing -
 * change what an existing slide says - and has no New, no Delete, no Rename and
 * no scheduling. The server enforces the same line (`signage_field_locked`);
 * this page just never offers what would be refused.
 *
 * ## Editing ON the slide, not in a form
 *
 * The slide is drawn full-width by the same renderer the wall runs, and only
 * the blocks the designer marked "unit staff can change this" are clickable -
 * outlined and labelled. Everything else is visibly locked. A form would ask a
 * nurse to map "Field 3" onto a picture of a screen in their head; this puts
 * the text box next to the words they can see.
 */

/** Which fields on this template a unit may change. */
export function unitFields(template: SignageTemplate): SignageTemplateField[] {
  const open = new Set(
    template.regions
      .filter((r) => r.binding.kind === "field" && r.binding.unitEditable)
      .map((r) => (r.binding as { field: string }).field)
  );
  return template.fields.filter((f) => open.has(f.key));
}

function fieldOf(region: SignageTemplateRegion): string | null {
  return region.binding.kind === "field" && region.binding.unitEditable
    ? region.binding.field
    : null;
}

function when(iso: string | undefined): string {
  if (!iso) return "";
  try {
    return new Intl.DateTimeFormat("sv-SE", {
      dateStyle: "short",
      timeStyle: "short",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export default function SignageUpdatesPage({
  premiseId,
}: {
  premiseId: string;
}) {
  const scope: SignageScopeRef = { kind: "premise", premiseId };
  const [templates, setTemplates] = useState<SignageTemplate[]>([]);
  const [slides, setSlides] = useState<SignageTemplateInstance[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let alive = true;
    void Promise.all([
      foyerApi.listSignageTemplatesIn(scope),
      foyerApi.listSignageSlidesIn(scope),
    ])
      .then(([t, s]) => {
        if (!alive) return;
        setTemplates(t);
        setSlides(s);
      })
      .catch(() => {
        if (!alive) return;
        setTemplates([]);
        setSlides([]);
      })
      .finally(() => alive && setLoaded(true));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [premiseId, tick]);

  const byId = useMemo(
    () => new Map(templates.map((t) => [t.id, t] as const)),
    [templates]
  );

  // Only slides with something the unit can actually change. A slide that is
  // entirely locked is the content owner's; listing it here would be a row
  // that opens onto nothing.
  const editable = slides.filter((s) => {
    const t = byId.get(s.templateId);
    return t ? unitFields(t).length > 0 : false;
  });

  const open = editable.find((s) => s.id === openId) ?? null;
  const openTemplate = open ? byId.get(open.templateId) : undefined;

  return (
    <PageContainer>
      <PageHeader
        title="Update screens"
        subtitle="Change the information on this unit's screens. Only the parts marked as yours can be changed."
      />

      {open && openTemplate ? (
        <SlideUpdater
          key={open.id}
          scope={scope}
          slide={open}
          template={openTemplate}
          onClose={() => setOpenId(null)}
          onSaved={() => setTick((t) => t + 1)}
        />
      ) : (
        <Stack gap={1.5}>
          {loaded && editable.length === 0 && (
            <Typography sx={{ fontSize: 14, color: tokens.inkMuted }}>
              There is nothing to update here yet. The person who set up your
              screens decides which parts you can change.
            </Typography>
          )}
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            }}
          >
            {editable.map((s) => {
              const t = byId.get(s.templateId)!;
              return (
                <Box
                  key={s.id}
                  role="button"
                  tabIndex={0}
                  aria-label={s.name}
                  data-testid={`update-slide-${s.id}`}
                  onClick={() => setOpenId(s.id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setOpenId(s.id);
                    }
                  }}
                  sx={{
                    border: `1px solid ${tokens.divider}`,
                    borderRadius: "10px",
                    overflow: "hidden",
                    cursor: "pointer",
                    "&:hover": { borderColor: tokens.brand },
                  }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      aspectRatio: `${t.canvas.width} / ${t.canvas.height}`,
                      background: t.background || "#000",
                    }}
                  >
                    <TemplateSlide
                      slide={previewSlide(t, s.values)}
                      timezone="Europe/Stockholm"
                    />
                  </Box>
                  <Box sx={{ p: 1.5 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                      {s.name}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: tokens.inkMuted }}>
                      {unitFields(t)
                        .map((f) => f.label)
                        .join(", ")}
                    </Typography>
                    {s.lastEditedBy && (
                      <Typography
                        sx={{ fontSize: 11, color: tokens.inkMuted, mt: 0.5 }}
                      >
                        Last changed by {s.lastEditedBy} {when(s.lastEditedAt)}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Stack>
      )}
    </PageContainer>
  );
}

/**
 * One slide, drawn large, with its unit-editable blocks clickable.
 *
 * The values sent back are the slide's WHOLE map with only unit fields
 * changed, because the PATCH replaces the map - and the server compares every
 * locked key against what is stored, so sending anything else would be
 * refused rather than silently overwrite the content owner's words.
 */
function SlideUpdater({
  scope,
  slide,
  template,
  onClose,
  onSaved,
}: {
  scope: SignageScopeRef;
  slide: SignageTemplateInstance;
  template: SignageTemplate;
  onClose: () => void;
  onSaved: () => void;
}) {
  const fields = unitFields(template);
  const [values, setValues] = useState<Record<string, string>>(slide.values);
  const [active, setActive] = useState<string>(fields[0]?.key ?? "");
  const [picking, setPicking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const dirty = fields.some(
    (f) => (values[f.key] ?? "") !== (slide.values[f.key] ?? "")
  );
  const field = fields.find((f) => f.key === active) ?? null;
  const { width, height } = template.canvas;

  const save = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await foyerApi.updateSignageSlideIn(scope, slide.id, { values });
      setSaved(true);
      onSaved();
    } catch (err) {
      setError(
        err instanceof Error && err.message.includes("locked")
          ? "Part of this slide is locked and was changed by someone else. Reload and try again."
          : "Could not save. Try again in a moment."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack gap={2}>
      <Stack direction="row" alignItems="center" gap={1}>
        <Button onClick={onClose}>← All screens</Button>
        <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
          {slide.name}
        </Typography>
      </Stack>

      <Stack direction={{ xs: "column", md: "row" }} gap={3}>
        <Box
          data-testid="update-canvas"
          sx={{
            position: "relative",
            flex: 1,
            minWidth: 0,
            aspectRatio: `${width} / ${height}`,
            borderRadius: "8px",
            overflow: "hidden",
            background: template.background || "#000",
          }}
        >
          <Box sx={{ position: "absolute", inset: 0 }}>
            <TemplateSlide
              slide={previewSlide(template, values)}
              timezone="Europe/Stockholm"
            />
          </Box>
          {/* Locked parts are dimmed, so what CAN be changed stands out. */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background: "rgba(0,0,0,0.35)",
              pointerEvents: "none",
            }}
          />
          {template.regions.map((region) => {
            const key = fieldOf(region);
            if (!key) return null;
            const selected = key === active;
            return (
              <Box
                key={region.id}
                role="button"
                aria-label={`Change ${
                  fields.find((f) => f.key === key)?.label ?? key
                }`}
                data-testid={`update-region-${region.id}`}
                onClick={() => setActive(key)}
                sx={{
                  position: "absolute",
                  left: `${(region.rect.x / width) * 100}%`,
                  top: `${(region.rect.y / height) * 100}%`,
                  width: `${(region.rect.w / width) * 100}%`,
                  height: `${(region.rect.h / height) * 100}%`,
                  // Punches the dimming back out over an editable block.
                  boxShadow: selected
                    ? `0 0 0 3px ${tokens.brand}`
                    : `0 0 0 2px rgba(255,255,255,0.9)`,
                  backdropFilter: "brightness(1.55)",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: 4,
                    left: 4,
                    fontSize: 11,
                    color: "#fff",
                    background: tokens.brand,
                    px: 0.75,
                    borderRadius: "4px",
                  }}
                >
                  ✎ {fields.find((f) => f.key === key)?.label ?? key}
                </Box>
              </Box>
            );
          })}
        </Box>

        <Stack gap={2} sx={{ width: { xs: "100%", md: 340 }, flexShrink: 0 }}>
          {field ? (
            <FieldControl
              key={field.key}
              field={field}
              value={values[field.key] ?? ""}
              onChange={(v) => {
                setSaved(false);
                setValues((prev) => ({ ...prev, [field.key]: v }));
              }}
              onPick={() => setPicking(true)}
            />
          ) : (
            <Typography sx={{ fontSize: 13, color: tokens.inkMuted }}>
              Click a highlighted part of the screen to change it.
            </Typography>
          )}
          {fields.length > 1 && (
            <Typography sx={{ fontSize: 12, color: tokens.inkMuted }}>
              You can change: {fields.map((f) => f.label).join(", ")}.
            </Typography>
          )}
          {error && <Alert severity="error">{error}</Alert>}
          {saved && !dirty && (
            <Alert severity="success">
              Saved. The screens update within a minute.
            </Alert>
          )}
          <Stack direction="row" gap={1}>
            <Button
              variant="contained"
              disabled={!dirty || saving}
              onClick={() => void save()}
              data-testid="update-save"
            >
              Save
            </Button>
            <Button
              disabled={!dirty || saving}
              onClick={() => setValues(slide.values)}
            >
              Undo changes
            </Button>
          </Stack>
        </Stack>
      </Stack>

      <AssetsPicker
        open={picking}
        onClose={() => setPicking(false)}
        onSelect={(url) => {
          if (field) {
            setValues((prev) => ({
              ...prev,
              [field.key]: withPicked(field.type, prev[field.key] ?? "", url),
            }));
          }
          setPicking(false);
        }}
      />
    </Stack>
  );
}
